import { Input, Modal, type ModalProps, Tile, TileGroup, Control, Centered } from "@snapchat/snap-design-system";
import { StatusType } from "@snapchat/snap-design-system/lib/components/Control/types";
import { PhoneIcon } from "@snapchat/snap-design-system-icons";
import styled from "@emotion/styled";
import { type FC, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import browserIcon from "../../assets/common/browser-icon.svg";
import { Image } from "../../common/components/Image";
import { GraphQLErrorService } from "../../auth/api";
import { type RouteParams, AbsoluteRoute } from "../../../core/routes";
import { useApps } from "../hooks/useApps";
import { AppPlatform } from "../../gql-generated";
import { MyLensesPlatformType } from "../../../gql-generated/types";

interface NewAppModalProps extends Omit<ModalProps, "children"> {
    handleNewApp: (appId: string) => void;
}

// Validation rules set via snap-canvas-web/snap-connect-developer-portal
const reservedNameExclusions = /^(?!.*snapchat|.*snap inc|.*bitmoji).*/i;
const minLength = 3;
const maxLength = 100;

const Unauthorized = "You are unauthorized, contact the organization admin to update your role to Organization Admin";
const DefaultError = "There was an error processing your request. Please try again";

const schema = [
    {
        test: (value: string) => value.length < minLength || value.length > maxLength,
        error: `App Name is too short (must be between ${minLength} and ${maxLength} characters)`,
    },
    {
        test: (value: string) => !reservedNameExclusions.test(value),
        error: "App Name is not formatted correctly",
    },
];

const platforms: {
    platform: MyLensesPlatformType;
    icon: JSX.Element;
}[] = [
    { platform: MyLensesPlatformType.PlatformTypeMobile, icon: <PhoneIcon size={24} /> },
    {
        platform: MyLensesPlatformType.PlatformTypeWeb,
        icon: <Image src={browserIcon} alt="web" widthPx={26} heightPx={26} isCircle={false} />,
    },
];

export const NewAppModal: FC<NewAppModalProps> = ({ visible, handleNewApp, onCancel }) => {
    const { createApp, isCreatingApp } = useApps({
        setNewAppCb: handleNewApp,
    });
    const { orgId } = useParams<RouteParams[AbsoluteRoute.ORG_APPS_CREATE]>();
    const [value, setValue] = useState<string>("");
    const [platformType, setPlatformType] = useState<MyLensesPlatformType | undefined>(undefined);
    const [error, setError] = useState("");
    const [statusType, setStatusType] = useState<StatusType | undefined>(undefined);

    const handleInput = useCallback((value: string) => {
        setError("");
        setStatusType(undefined);
        setValue(value);
    }, []);

    const validate = useCallback((value: string) => {
        const error = schema.find((validation) => validation.test(value))?.error ?? "";

        if (error) {
            setError(error);
            setStatusType(StatusType.ERROR);
        }

        return error;
    }, []);

    const onSubmit = useCallback(async () => {
        const error = validate(value);

        if (!error) {
            setStatusType(StatusType.SUCCESS);
            if (platformType && orgId) {
                try {
                    const { errors } = await createApp({
                        variables: {
                            platformType: platformType,
                            appName: value,
                            orgId,
                        },
                    });

                    if (errors != null) {
                        // errors is has an incorrect gql type
                        throw new Error(errors as any);
                    }

                    setStatusType(undefined);
                    setPlatformType(undefined);
                    setValue("");
                } catch (e) {
                    setStatusType(StatusType.ERROR);
                    if (e instanceof Error && e?.message.includes(GraphQLErrorService.permissionDenied)) {
                        setError(Unauthorized);
                    } else {
                        setError(DefaultError);
                    }
                }
            }
        } else {
            setError(DefaultError);
        }
    }, [validate, value, createApp, orgId, platformType]);

    const handleCancel = () => {
        setValue("");
        setPlatformType(undefined);
        setError("");
        setStatusType(undefined);
        onCancel?.();
    };

    return (
        <Modal
            title="New Snap Kit App"
            visible={visible}
            onAction={onSubmit}
            actionText="Create App"
            isActionButtonPending={isCreatingApp}
            isActionButtonDisabled={!platformType}
            onCancel={handleCancel}
            cancelText="Back"
            data-testid="create-app-modal"
            isCancelButtonDisabled={isCreatingApp}
        >
            <ModalContent>
                <Input
                    label="App Name"
                    value={value}
                    onChange={handleInput}
                    status={error}
                    statusType={statusType}
                    disabled={isCreatingApp}
                    data-testid="new-app-input"
                    autoFocus
                ></Input>
                <Control label="Target Platform">
                    <TileGroup emphasized value={platformType}>
                        {platforms.map(({ platform, icon }) => (
                            <Tile
                                value={platform}
                                key={platform}
                                onClick={() => {
                                    setPlatformType(platform);
                                }}
                                emphasized
                            >
                                <PlatformContainer>
                                    {icon}
                                    {AppPlatform[platform]}
                                </PlatformContainer>
                            </Tile>
                        ))}
                    </TileGroup>
                </Control>
            </ModalContent>
        </Modal>
    );
};

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const PlatformContainer = styled(Centered)`
    display: flex;
    flex-direction: column;
    margin: 5px 0;
`;
